import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
   Card,
   FormControl,
   InputAdornment,
   InputLabel,
   MenuItem,
   Popover,
   Select,
   Typography,
   useMediaQuery,
   useTheme,
} from '@mui/material'
import React, { useCallback, useState, useEffect } from 'react'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import './datepicker-styles.css'
import DatePicker, { registerLocale } from 'react-datepicker'
import { CalendarTodayOutlined, Check, Edit, Search } from '@mui/icons-material'
import 'react-datepicker/dist/react-datepicker.css'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import { enUS } from 'date-fns/locale/en-US'
import { useQueryClient } from 'react-query'
import Tooltip from '@mui/material/Tooltip'
import Switch from '@mui/material/Switch'
import { pl as plPL } from 'date-fns/locale/pl'
import Box from '@mui/material/Box'
import { CSVLink } from 'react-csv'
import moment from 'moment'

import { useChangeOrderNameMutation, useOrdersHistory, useSearchOrdersHistory } from 'shared/store/useOrdersHistory'
import { useSettingsMutation, useSettingsQuery } from 'shared/store/useSettings'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useLocationsQuery } from 'shared/store/useLocations'
import { Order } from 'shared/store/useOrdersHistory/types'
import { useProfileQuery } from 'shared/store/useProfile'
import CustomTable from 'shared/components/CustomTable'

import { ChangeNameDetailsType, PrintType, StoredOrderDetails } from './types'
import { StyledClientPrintIcon, StyledFullPrintIcon } from './styles'
import OrderDetails from './components/OrderDetails'
import OrderPrint from './components/OrderPrint'
import { pl, en, de } from './locales'

const sorterEnum: { [key: string]: 'ORDER_DATE' | 'CART_NAME' | 'VALUE' | 'CLIENT_VALUE_WITH_SERVICES' | 'USERNAME' } =
   {
      orderDate: 'ORDER_DATE',
      cartName: 'CART_NAME',
      value: 'VALUE',
      clientValueWithServices: 'CLIENT_VALUE_WITH_SERVICES',
      userName: 'USERNAME',
   }

function OrdersHistory() {
   const { i18n, t } = useTranslation(['TRANSLATION', 'ORDERS_HISTORY'])
   i18n.addResourceBundle('pl-PL', 'ORDERS_HISTORY', pl)
   i18n.addResourceBundle('en-EN', 'ORDERS_HISTORY', en)
   i18n.addResourceBundle('de-DE', 'ORDERS_HISTORY', de)
   const [searchPhrase, setSearchPhrase] = useState('')
   const [page, setPage] = useState(0)
   const [pageSize, setPageSize] = useState(
      window.localStorage.getItem('itemsPerPage') ? Number(window.localStorage.getItem('itemsPerPage')) : 25
   )
   const [filterByStatus, setFilterByStatus] = useState<PropType<OrderType, 'status'>[]>([])
   const [openDetails, handleOpenDetails] = useState<string[]>()
   const [printDetails, handleOpenPrint] = useState<string[]>()
   const [printType, setPrintType] = useState<PrintType>('clientPrint')
   const [savedOrderDetails, handleOrdersDetails] = useState<StoredOrderDetails[]>([])
   const [startDate, setStartDate] = useState<Date>(() => {
      const date = new Date()
      date.setDate(date.getDate() - 7)
      return date
   })
   const [sortBy, setSortBy] = useState<string>('orderDate')
   const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>('desc')

   const [endDate, setEndDate] = useState<Date>(new Date())
   const { data, isLoading, isError, error } = useOrdersHistory(
      page,
      pageSize,
      moment(startDate).format('YYYY-MM-DD'),
      moment(endDate).format('YYYY-MM-DD')
   )

   const customPl = {
      ...plPL,
      localize: {
         ...plPL.localize,
         month: (n: number) => {
            const customMonths = [
               'Styczeń',
               'Luty',
               'Marzec',
               'Kwiecień',
               'Maj',
               'Czerwiec',
               'Lipiec',
               'Sierpień',
               'Wrzesień',
               'Październik',
               'Listopad',
               'Grudzień',
            ]
            return customMonths[n]
         },
         day: (n: number, options?: { width?: string; context?: string }) => {
            const shortDays = ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So']
            const longDays = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota']
            const narrowDays = ['N', 'P', 'W', 'Ś', 'C', 'P', 'S']

            const width = options?.width || 'wide'

            switch (width) {
               case 'narrow':
                  return narrowDays[n]
               case 'short':
                  return shortDays[n]
               case 'abbreviated':
                  return shortDays[n]
               case 'wide':
               default:
                  return longDays[n]
            }
         },
      },
   }

   registerLocale('pl-PL', customPl)
   registerLocale('en-EN', enUS)

   const [searchParams, setSearchParams] = useSearchParams()
   const { data: settingsData } = useSettingsQuery()
   const { mutate: mutateSettings, isLoading: isSettingsMutateLoading } = useSettingsMutation(true)
   const { data: profileSettings } = useProfileQuery()
   const { data: locationsData } = useLocationsQuery()
   const location = useLocation()
   const queryClient = useQueryClient()
   const theme = useTheme()
   const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))
   const [isDateRangeError, setIsDateRangeError] = useState(false)
   const navigate = useNavigate()
   const [dataLoading, setDataLoading] = useState(false)
   const [changeNameDetails, setChangeNameDetails] = useState<ChangeNameDetailsType>({
      orderName: '',
      orderUuid: '',
   })
   const [openCalendar, setOpenCalendar] = useState<{
      open: boolean
      variant: 'start' | 'end'
      anchor: HTMLElement | null
   }>({
      open: false,
      variant: 'start',
      anchor: null,
   })
   const { mutate, isLoading: isMutateLoading } = useSearchOrdersHistory()
   const { mutate: changeOrderNameMutate, isLoading: changeOrderNameMutateIsLoading } = useChangeOrderNameMutation()

   const activeLocationSettings = locationsData?.locations.find(
      (item) => item.locationSettings.uuid === profileSettings?.locationUuid
   )

   function handleOnSearch(
      newPage: number,
      newPageSize: number,
      newStartDate?: Date,
      newEndDate?: Date,
      resetFilters?: boolean
   ) {
      setDataLoading(true)
      if (!isLoading) {
         if (resetFilters) {
            mutate(
               {
                  page: newPage,
                  pageSize: newPageSize,
                  searchValue: '',
                  toDate: newEndDate ? moment(newEndDate).format('YYYY-MM-DD') : moment(endDate).format('YYYY-MM-DD'),
                  fromDate: newStartDate
                     ? moment(newStartDate).format('YYYY-MM-DD')
                     : moment(startDate).format('YYYY-MM-DD'),
                  sortOrder: 'DESC',
                  sortField: 'ORDER_DATE',
               },
               {
                  onSettled: () => {
                     setDataLoading(false)
                     setSortBy('orderDate')
                     setSortDirection('desc')
                     setPage(0)
                  },
               }
            )
         } else {
            mutate(
               {
                  page: newPage,
                  toDate: newEndDate ? moment(newEndDate).format('YYYY-MM-DD') : moment(endDate).format('YYYY-MM-DD'),
                  fromDate: newStartDate
                     ? moment(newStartDate).format('YYYY-MM-DD')
                     : moment(startDate).format('YYYY-MM-DD'),
                  pageSize: newPageSize,
                  searchValue: searchPhrase,
                  filterStatus: filterByStatus.length > 0 ? filterByStatus : undefined,
                  sortOrder: sortDirection ? (sortDirection.toUpperCase() as 'ASC' | 'DESC') : 'DESC',
                  sortField: sortBy ? sorterEnum[sortBy] : 'ORDER_DATE',
               },
               {
                  onSettled: () => {
                     setDataLoading(false)
                     setPage(0)
                  },
               }
            )
         }
         setOpenCalendar({ open: false, variant: 'start', anchor: null })
      }
   }

   function updateDetailsStore(newRecord: StoredOrderDetails, remove?: boolean) {
      if (remove) {
         const newDetails = savedOrderDetails.filter((item) => item.id !== newRecord.id)
         handleOrdersDetails([...newDetails])
      } else {
         handleOrdersDetails((prevState) => [...prevState, newRecord])
      }
   }

   function getDefaultStartDate() {
      const date = new Date()
      date.setDate(date.getDate() - 7)
      return date
   }

   function handleGroupingChange() {
      if (settingsData) {
         const newBuyNowOrdersGroup = settingsData.userSettings.preferences.buyNowOrdersGroup !== 'true'
         mutateSettings(
            {
               ...settingsData,
               userSettings: {
                  ...settingsData.userSettings,
                  preferences: {
                     ...settingsData.userSettings.preferences,
                     showProductNamesInCart: settingsData.userSettings.preferences.showProductNamesInCart === 'true',
                     buyNowOrdersGroup: newBuyNowOrdersGroup,
                     priceDifferences: settingsData.userSettings.preferences.priceDifferences === 'true',
                     displayMinPopup: settingsData.userSettings.preferences.displayMinPopup === 'true',
                     dontShowLocationDialog: settingsData.userSettings.preferences.dontShowLocationDialog === 'true',
                  },
               },
            },
            {
               onSuccess: () => {
                  handleOnSearch(0, pageSize)
                  handleOrdersDetails(
                     savedOrderDetails.filter(
                        (item) => item.orderType !== 'BUY_NOW' && item.orderType !== 'GROUPED_BUY_NOW'
                     )
                  )
               },
            }
         )
      }
   }

   useEffect(() => {
      if (isError) {
         navigate('/dashboard/error-page', {
            state: {
               variant: 'backend',
               path: location.pathname,
               error: {
                  message: error,
                  stack: 'unknown',
               },
               props: {
                  startDate: startDate.toString(),
                  endDate: endDate.toString(),
                  searchPhrase,
               },
            },
         })
      }
   }, [isError, isLoading])

   useEffect(() => {
      queryClient.invalidateQueries('userProfile')
   }, [])

   function getHighlightColor(orderStatus: PropType<OrderType, 'status'>) {
      switch (orderStatus) {
         case 'UNSET':
            return 'rgb(255, 136, 0,0.7)'

         case 'FAILED':
            return 'rgba(255,0,0,0.5)'
         case 'PARTIALLY_SUCCESSFUL':
            return 'rgb(255, 136, 0,0.7)'
         case 'SUCCESSFUL':
         default:
            return 'transparent'
      }
   }

   function handlePrint(order: Order, event: React.MouseEvent<HTMLButtonElement, MouseEvent>, typeOfPrint: PrintType) {
      event.preventDefault()
      event.stopPropagation()
      handleOpenPrint(order.orderUuids)
      setPrintType(typeOfPrint)
   }

   function renderCartName(orderType?: PropType<Order, 'orderType'>, cartName?: string, orderDate?: string) {
      const reg = /^(?:0?[1-9]|[12][0-9]|3[01])\/(?:0?[1-9]|1[012])\/\d{4}$/
      if (cartName) {
         if (orderType === 'BUY_NOW' && reg.test(cartName)) {
            return i18n.t('ORDERS_HISTORY:buyNowCartName', { date: orderDate })
         }
      }
      return <Typography>{cartName && cartName?.length > 25 ? `${cartName.slice(0, 25)}...` : cartName}</Typography>
   }

   function handleChangeOrderName() {
      if (changeNameDetails.orderUuid) {
         changeOrderNameMutate(
            { orderName: changeNameDetails.orderName, orderUuid: changeNameDetails.orderUuid },
            {
               onSuccess: () => {
                  setChangeNameDetails({ orderUuid: '', orderName: '' })
               },
            }
         )
      }
   }

   function generateCSV() {
      const CSVData = []
      if (data) {
         const title: string[] = [
            t('TRANSLATION:columnNames.date'),
            t('TRANSLATION:columnNames.cartName'),
            t('TRANSLATION:columnNames.comment'),
            t('TRANSLATION:columnNames.sum'),
            t('TRANSLATION:columnNames.clientValueWithServices'),
            t('TRANSLATION:columnNames.phoneNumber'),
            t('TRANSLATION:columnNames.userName'),
         ]
         CSVData.push(title)
         data.orders.map((item) => {
            return CSVData.push([
               item.orderDate,
               item.cartName,
               item.cartDescription,
               item.formattedValue,
               item.formattedClientValue,
               item.phoneNumber !== null ? item.phoneNumber : '',
               item.userName,
            ])
         })
      }

      const date = new Date()

      return (
         <CSVLink
            filename={t('ORDERS_HISTORY:csvFileName', { date: date.toLocaleString(i18n.language) })}
            data={CSVData}
         >
            <ContainedButton variant="contained">{t('ORDERS_HISTORY:generateCSV')}</ContainedButton>
         </CSVLink>
      )
   }

   const getTitle = useCallback(() => {
      const orderUuids = openDetails || printDetails
      const target = data?.orders.find((item) => item.orderUuids === orderUuids)
      if (target) {
         if (target.orderType === 'GROUPED_BUY_NOW') {
            return t('ORDERS_HISTORY:buyNowOrderPrintTitle', {
               orderDate: target?.orderDate,
            })
         }
         return t('ORDERS_HISTORY:orderPrintTitle', {
            orderName: target?.cartName,
            orderDate: target?.orderDate,
            userName: target.userName,
         })
      }
      return ''
   }, [openDetails, printDetails])

   useEffect(() => {
      const uuid = searchParams.get('uuid')
      if (data) {
         if (uuid) {
            const tempData = data.orders.find((item) => item.orderUuids.includes(uuid))
            if (tempData) {
               handleOpenDetails(tempData.orderUuids)
               searchParams.delete('uuid')
               setSearchParams(searchParams)
            }
         }
      }
   }, [searchParams.get('uuid'), data])

   useEffect(() => {
      if (moment(endDate).isBefore(moment(startDate))) {
         const newDate = new Date(endDate)
         newDate.setDate(newDate.getDate() - 1)
         setStartDate(newDate)
      }
   }, [endDate])

   useEffect(() => {
      if (moment(endDate).diff(moment(startDate), 'days') > 365) {
         setIsDateRangeError(true)
      } else {
         setIsDateRangeError(false)
      }
   }, [startDate, endDate])

   useEffect(() => {
      console.log(savedOrderDetails)
   }, [savedOrderDetails])

   return (
      <Box>
         {!isLoading ? (
            <>
               <div style={{ width: '100%' }}>
                  {data && Array.isArray(data.orders) && (
                     <Card>
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                              gap: { lg: 1, md: 1, xs: 2 },
                              my: 2,
                              px: 2,
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                                 width: '100%',
                                 gap: { lg: 2, md: 2, xs: 2 },
                                 alignItems: 'center',
                              }}
                           >
                              <TextField
                                 variant="outlined"
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleOnSearch(0, pageSize)
                                 }}
                                 label={t('TRANSLATION:searchAndFilters.searchPhrase')}
                                 fullWidth={isSmallScreen}
                                 sx={{
                                    minWidth: { lg: '300px', md: '200px', xs: 'auto' },
                                 }}
                                 onChange={(e) => setSearchPhrase(e.target.value)}
                                 value={searchPhrase}
                              />
                              <Box
                                 sx={{
                                    display: 'flex',
                                    width: { lg: 'auto', md: 'auto', xs: '100%' },
                                    flexDirection: 'row',
                                    gap: 1,
                                    justifyContent: { lg: 'normal', md: 'normal', xs: 'center' },
                                    alignItems: 'center',
                                 }}
                              >
                                 <OutlinedButton
                                    disabled={dataLoading || isMutateLoading || isDateRangeError}
                                    variant="outlined"
                                    onClick={() => handleOnSearch(0, pageSize)}
                                 >
                                    {t('ORDERS_HISTORY:confirm')}
                                    <Box component={Search} sx={{ width: 20, height: 20 }} />
                                 </OutlinedButton>
                                 <ContainedButton
                                    sx={{
                                       minWidth: 'max-content',
                                    }}
                                    variant="contained"
                                    disabled={dataLoading || isMutateLoading}
                                    onClick={() => {
                                       setSearchPhrase('')
                                       setFilterByStatus([])
                                       setEndDate(new Date())
                                       setStartDate(getDefaultStartDate())
                                       handleOnSearch(0, pageSize, getDefaultStartDate(), new Date(), true)
                                    }}
                                 >
                                    {t('TRANSLATION:searchAndFilters.clearFilters')}
                                 </ContainedButton>
                              </Box>
                              <Box
                                 sx={{
                                    display: 'flex',
                                    width: { lg: 'auto', md: 'auto', xs: '100%' },
                                    flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                                    gap: { lg: 2, md: 2, xs: 2 },
                                    alignItems: 'center',
                                 }}
                              >
                                 <TextField
                                    label="Data od"
                                    disabled={isMutateLoading}
                                    sx={{ width: { lg: 130, md: 130, xs: 'none' }, flex: 1 }}
                                    slotProps={{
                                       input: {
                                          readOnly: true,
                                          startAdornment: (
                                             <InputAdornment position="start">
                                                <CalendarTodayOutlined />
                                             </InputAdornment>
                                          ),
                                       },
                                    }}
                                    value={moment(startDate).format('YYYY-MM-DD')}
                                    fullWidth
                                    onClick={(e) =>
                                       setOpenCalendar({ open: true, variant: 'start', anchor: e.currentTarget })
                                    }
                                 />
                                 <TextField
                                    label="Data do"
                                    disabled={isMutateLoading}
                                    sx={{ width: { lg: 130, md: 130, xs: 'none' }, flex: 1 }}
                                    slotProps={{
                                       input: {
                                          readOnly: true,
                                          startAdornment: (
                                             <InputAdornment position="start">
                                                <CalendarTodayOutlined />
                                             </InputAdornment>
                                          ),
                                       },
                                    }}
                                    fullWidth
                                    value={moment(endDate).format('YYYY-MM-DD')}
                                    onClick={(e) =>
                                       setOpenCalendar({ open: true, variant: 'end', anchor: e.currentTarget })
                                    }
                                 />
                                 {/* start datepicker */}
                                 <Popover
                                    anchorOrigin={{
                                       vertical: 'bottom',
                                       horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                       vertical: 'top',
                                       horizontal: 'left',
                                    }}
                                    onClose={() => setOpenCalendar({ open: false, variant: 'start', anchor: null })}
                                    open={openCalendar.open && openCalendar.variant === 'start'}
                                    anchorEl={openCalendar.anchor}
                                 >
                                    <DatePicker
                                       locale={i18n.language}
                                       inline
                                       disabled={isMutateLoading}
                                       selected={startDate}
                                       onChange={(date) => {
                                          if (date) {
                                             setStartDate(date)
                                          }
                                       }}
                                    />
                                 </Popover>
                                 {/* end datepicker */}
                                 <Popover
                                    anchorOrigin={{
                                       vertical: 'bottom',
                                       horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                       vertical: 'top',
                                       horizontal: 'left',
                                    }}
                                    onClose={() => setOpenCalendar({ open: false, variant: 'start', anchor: null })}
                                    open={openCalendar.open && openCalendar.variant === 'end'}
                                    anchorEl={openCalendar.anchor}
                                 >
                                    <DatePicker
                                       locale={i18n.language}
                                       inline
                                       disabled={isMutateLoading}
                                       selected={endDate}
                                       onChange={(date) => {
                                          if (date) {
                                             setEndDate(date)
                                          }
                                       }}
                                    />
                                 </Popover>
                              </Box>
                              <Box
                                 sx={{
                                    display: 'flex',
                                    width: { lg: '40%', md: '50%', xs: '100%' },
                                    flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                                    gap: 1,
                                    alignItems: 'center',
                                 }}
                              >
                                 <FormControl
                                    fullWidth
                                    component="fieldset"
                                    sx={{ flex: 1, maxWidth: { lg: 170, md: 170, xs: 'none' } }}
                                    variant="outlined"
                                 >
                                    <InputLabel>{t('ORDERS_HISTORY:orderStatusFilter')}</InputLabel>
                                    <Select
                                       disabled={isMutateLoading}
                                       multiple
                                       sx={{ maxWidth: { lg: 170, md: 170, xs: 'none' } }}
                                       fullWidth
                                       value={filterByStatus}
                                       onChange={(e) =>
                                          setFilterByStatus(
                                             e.target.value as unknown as PropType<OrderType, 'status'>[]
                                          )
                                       }
                                       label={t('ORDERS_HISTORY:orderStatusFilter')}
                                    >
                                       <MenuItem value="SUCCESSFUL">{t('ORDERS_HISTORY:successStatus')}</MenuItem>
                                       <MenuItem value="PARTIALLY_SUCCESSFUL">
                                          {t('ORDERS_HISTORY:partiallySuccess')}
                                       </MenuItem>
                                       <MenuItem value="FAILED">{t('ORDERS_HISTORY:failedStatus')}</MenuItem>
                                    </Select>
                                 </FormControl>
                                 <FormControlLabel
                                    sx={{ flex: 1 }}
                                    control={
                                       <Switch
                                          checked={settingsData?.userSettings.preferences.buyNowOrdersGroup === 'true'}
                                          onChange={handleGroupingChange}
                                          disabled={isSettingsMutateLoading || isMutateLoading}
                                       />
                                    }
                                    label={t('ORDERS_HISTORY:groupBuyNows')}
                                 />
                              </Box>
                           </Box>
                           {isDateRangeError && (
                              <Typography variant="h6" color="error">
                                 {t('ORDERS_HISTORY:dateRangeError')}
                              </Typography>
                           )}
                        </Box>
                        <CustomTable
                           disableInternalSorting
                           externalSortBy={sortBy}
                           externalSortDirection={sortDirection}
                           onSortChange={(by, dir) => {
                              setPage(0)
                              setSortBy(by || 'orderDate')
                              setSortDirection(dir)
                              mutate(
                                 {
                                    page: 0,
                                    pageSize,
                                    searchValue: searchPhrase,
                                    filterStatus: filterByStatus.length > 0 ? filterByStatus : undefined,
                                    sortField: by ? sorterEnum[by] : undefined,
                                    sortOrder: dir ? (dir.toUpperCase() as 'ASC' | 'DESC') : undefined,
                                    toDate: endDate
                                       ? moment(endDate).format('YYYY-MM-DD')
                                       : moment(endDate).format('YYYY-MM-DD'),
                                    fromDate: startDate
                                       ? moment(startDate).format('YYYY-MM-DD')
                                       : moment(startDate).format('YYYY-MM-DD'),
                                 },
                                 {
                                    onSettled: () => {
                                       setDataLoading(false)
                                    },
                                 }
                              )
                           }}
                           dataSize={data.totalElements}
                           dependencies={[filterByStatus, searchPhrase, pageSize, startDate, endDate, settingsData]}
                           pagingVariants={[10, 25, 50]}
                           activePage={page}
                           customChangePage={(activePage, rowsPerPage) => {
                              setPage(activePage)
                              setPageSize(rowsPerPage)
                              handleOnSearch(activePage, rowsPerPage)
                           }}
                           customChangeRowsPerPage={(activePage, rowsPerPage) => {
                              setPage(0)
                              setPageSize(rowsPerPage)
                              handleOnSearch(activePage, rowsPerPage)
                           }}
                           customNoResultsText={dataLoading ? <CircularProgress /> : t('TRANSLATION:noResults')}
                           onChangeRowsPerPage={(rowsPerPage) =>
                              window.localStorage.setItem('itemsPerPage', rowsPerPage.toString())
                           }
                           rowsPerPage={pageSize}
                           rows={data.orders}
                           onRowClick={(row: Order) => handleOpenDetails(row.orderUuids)}
                           customRowStyles={(row: Order) => ({
                              ...((row.status === 'FAILED' || row.status === 'PARTIALLY_SUCCESSFUL') && {
                                 backgroundColor: getHighlightColor(row.status ?? 'SUCCESSFUL'),
                                 '&:hover': {
                                    backgroundColor: getHighlightColor(row.status ?? 'SUCCESSFUL'),
                                 },
                                 textAlign: 'center',
                                 cursor: 'pointer',
                              }),
                           })}
                           columns={[
                              {
                                 name: 'date',
                                 renderCell: (row: Order) => row.orderDate,
                                 sortable: true,
                                 sortLabel: 'orderDate',
                                 sortType: 'date',
                                 mainOnMobile: true,
                              },
                              {
                                 name: 'cartName',
                                 hidden: !isSmallScreen,
                                 sortable: true,
                                 sortLabel: 'cartName',
                                 sortType: 'string',
                                 mainOnMobile: true,
                                 renderCell: (row: Order) => (
                                    <Box
                                       sx={{
                                          ...(isSmallScreen && {
                                             maxWidth: 100,
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                          }),
                                       }}
                                    >
                                       {renderCartName(row.orderType, row.cartName, row.orderDate)}
                                    </Box>
                                 ),
                              },
                              {
                                 name: 'client',
                                 hidden: isSmallScreen,
                                 sortable: true,
                                 sortLabel: 'clientDetails',
                                 mainOnMobile: false,
                                 renderCell: (row: Order) => row.clientDetails,
                              },
                              {
                                 name: 'cartName',
                                 hidden: isSmallScreen,
                                 renderCell: (row: Order) =>
                                    row.orderUuids[0] === changeNameDetails.orderUuid ? (
                                       <Box
                                          onKeyDown={(e) => {
                                             if (e.key === 'Escape') {
                                                setChangeNameDetails({
                                                   orderName: '',
                                                   orderUuid: '',
                                                })
                                             }
                                             if (e.key === 'Enter') {
                                                handleChangeOrderName()
                                             }
                                          }}
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             gap: 1,
                                          }}
                                       >
                                          <TextField
                                             label={t('TRANSLATION:columnNames.cartName')}
                                             defaultValue={changeNameDetails.orderName}
                                             disabled={changeOrderNameMutateIsLoading}
                                             inputProps={{
                                                maxLength: 50,
                                             }}
                                             multiline
                                             onClick={(e) => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                             }}
                                             onChange={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                setChangeNameDetails({
                                                   ...changeNameDetails,
                                                   orderName: e.target.value,
                                                })
                                             }}
                                          />
                                          <IconButton
                                             disabled={changeOrderNameMutateIsLoading}
                                             onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                if (row.cartName !== changeNameDetails.orderName) {
                                                   handleChangeOrderName()
                                                } else {
                                                   setChangeNameDetails({
                                                      orderUuid: '',
                                                      orderName: '',
                                                   })
                                                }
                                             }}
                                             sx={{ '&:hover': { color: 'green', fontWeight: 'bold' } }}
                                          >
                                             <Check />
                                          </IconButton>
                                       </Box>
                                    ) : (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             gap: 1,
                                          }}
                                       >
                                          {renderCartName(row.orderType, row.cartName, row.orderDate)}
                                          {row.orderType !== 'GROUPED_BUY_NOW' && (
                                             <IconButton
                                                onClick={(e) => {
                                                   e.preventDefault()
                                                   e.stopPropagation()
                                                   setChangeNameDetails({
                                                      orderUuid: row.orderUuids[0] || '',
                                                      orderName: row.cartName || '',
                                                   })
                                                }}
                                             >
                                                <Edit />
                                             </IconButton>
                                          )}
                                       </Box>
                                    ),
                                 sortable: true,
                                 sortLabel: 'cartName',
                                 sortType: 'string',
                                 mainOnMobile: true,
                              },
                              {
                                 name: 'cartName',
                                 hidden: !isSmallScreen,
                                 onlyOnMobile: true,
                                 renderCell: (row: Order) =>
                                    row.orderUuids[0] === changeNameDetails.orderUuid ? (
                                       <Box
                                          onKeyDown={(e) => {
                                             if (e.key === 'Escape') {
                                                setChangeNameDetails({
                                                   orderName: '',
                                                   orderUuid: '',
                                                })
                                             }
                                             if (e.key === 'Enter') {
                                                handleChangeOrderName()
                                             }
                                          }}
                                          sx={{
                                             width: '100%',
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             gap: 1,
                                          }}
                                       >
                                          <TextField
                                             label={t('TRANSLATION:columnNames.cartName')}
                                             defaultValue={changeNameDetails.orderName}
                                             disabled={changeOrderNameMutateIsLoading}
                                             inputProps={{
                                                maxLength: 50,
                                             }}
                                             multiline
                                             onClick={(e) => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                             }}
                                             onChange={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                setChangeNameDetails({
                                                   ...changeNameDetails,
                                                   orderName: e.target.value,
                                                })
                                             }}
                                          />
                                          <IconButton
                                             disabled={changeOrderNameMutateIsLoading}
                                             onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                if (row.cartName !== changeNameDetails.orderName) {
                                                   handleChangeOrderName()
                                                } else {
                                                   setChangeNameDetails({
                                                      orderUuid: '',
                                                      orderName: '',
                                                   })
                                                }
                                             }}
                                             sx={{ '&:hover': { color: 'green', fontWeight: 'bold' } }}
                                          >
                                             <Check />
                                          </IconButton>
                                       </Box>
                                    ) : (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             gap: 1,
                                             fontSize: 11,
                                          }}
                                       >
                                          <Box
                                             sx={{
                                                flex: 1,
                                                fontSize: 11,
                                             }}
                                          >
                                             {renderCartName(row.orderType, row.cartName, row.orderDate)}
                                          </Box>
                                          {row.orderType !== 'GROUPED_BUY_NOW' && (
                                             <IconButton
                                                onClick={(e) => {
                                                   e.preventDefault()
                                                   e.stopPropagation()
                                                   setChangeNameDetails({
                                                      orderUuid: row.orderUuids[0] || '',
                                                      orderName: row.cartName || '',
                                                   })
                                                }}
                                             >
                                                <Edit />
                                             </IconButton>
                                          )}
                                       </Box>
                                    ),
                              },
                              {
                                 name: 'orderType',
                                 renderCell: (row: Order) => t(`ORDERS_HISTORY:orderType.${row.orderType}`),
                              },
                              {
                                 name: 'comment',
                                 renderCell: (row: Order) => (
                                    <Tooltip
                                       title={
                                          row.cartDescription === 'INTER_CARS_EXTERNAL'
                                             ? t('ORDERS_HISTORY:comment.interCarsExternal')
                                             : row.cartDescription
                                       }
                                    >
                                       <Box
                                          sx={{
                                             fontSize: 11,

                                             maxWidth: 200,
                                             overflow: 'hidden',
                                             m: '0 auto',
                                             textOverflow: 'ellipsis',
                                             ...(isSmallScreen && {
                                                maxWidth: 100,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                             }),
                                          }}
                                       >
                                          {row.cartDescription === 'INTER_CARS_EXTERNAL'
                                             ? t('ORDERS_HISTORY:comment.interCarsExternal')
                                             : row.cartDescription}
                                       </Box>
                                    </Tooltip>
                                 ),
                              },
                              {
                                 name: 'phoneOrEmail',
                                 renderCell: (row: Order) => (
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                       {row.phoneNumber && <Typography>{row.phoneNumber}</Typography>}
                                       {row.clientEmail && <Typography>{row.clientEmail}</Typography>}
                                    </Box>
                                 ),
                              },
                              {
                                 name: 'sum',
                                 renderCell: (row: Order) => row.formattedValue,
                                 sortable: true,
                                 sortLabel: 'value',
                                 sortType: 'number',
                                 showPriceType: true,
                                 mainOnMobile: true,
                              },
                              {
                                 name: 'clientValueWithServices',
                                 renderCell: (row: Order) => row.formattedClientValueWithServices,
                                 sortable: true,
                                 sortLabel: 'clientValueWithServices',
                                 sortType: 'number',
                                 showPriceType: true,
                              },
                              {
                                 name: 'orderingPerson',
                                 renderCell: (row: Order) => row.userName,
                                 sortable: true,
                                 sortLabel: 'userName',
                                 sortType: 'string',
                              },
                              {
                                 name: '',
                                 renderCell: (row: Order) => (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                       {row.orderType !== 'BASELINKER' && (
                                          <Tooltip title={t('ORDERS_HISTORY:clientPrint') || ''}>
                                             <IconButton
                                                onClick={(e) => {
                                                   handlePrint(row, e, 'clientPrint')
                                                }}
                                             >
                                                <StyledClientPrintIcon />
                                             </IconButton>
                                          </Tooltip>
                                       )}
                                       <Tooltip title={t('ORDERS_HISTORY:fullPrint') || ''}>
                                          <IconButton
                                             onClick={(e) => {
                                                handlePrint(row, e, 'fullPrint')
                                             }}
                                          >
                                             <StyledFullPrintIcon />
                                          </IconButton>
                                       </Tooltip>
                                    </Box>
                                 ),
                              },
                              {
                                 name: '',
                                 mainOnMobile: true,
                                 onlyOnMobile: true,
                                 preventOnClick: true,
                                 renderCell: (row: Order) => (
                                    <ContainedButton
                                       variant="contained"
                                       onClick={() => handleOpenDetails(row.orderUuids)}
                                    >
                                       {t('TRANSLATION:showProductsButton')}
                                    </ContainedButton>
                                 ),
                              },
                           ]}
                        />
                     </Card>
                  )}
               </div>
               {!isSmallScreen && data && data.orders.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, width: '100%' }}>
                     {generateCSV()}
                  </Box>
               )}
               {!!openDetails && (
                  <OrderDetails
                     orderUuids={openDetails}
                     handleOnSearch={() => handleOnSearch(page, pageSize)}
                     open={!!openDetails}
                     orderStatus={data?.orders.find((item) => item.orderUuids === openDetails)?.status}
                     title={getTitle()}
                     clientName={data?.orders.find((item) => item.orderUuids === openDetails)?.clientDetails}
                     contactName={data?.orders.find((item) => item.orderUuids === openDetails)?.clientContactDetails}
                     onClose={() => handleOpenDetails(undefined)}
                     priceType={activeLocationSettings?.markupSettings.priceType}
                     details={savedOrderDetails}
                     formattedGrossValue={
                        data?.orders.find((item) => item.orderUuids === openDetails)?.formattedGrossValue
                     }
                     formattedNetValue={data?.orders.find((item) => item.orderUuids === openDetails)?.formattedNetValue}
                     formattedServicesValue={
                        data?.orders.find((item) => item.orderUuids === openDetails)?.formattedAdditionalServicesValue
                     }
                     netDeliveryCost={data?.orders.find((item) => item.orderUuids === openDetails)?.netDeliveryCost}
                     grossDeliveryCost={data?.orders.find((item) => item.orderUuids === openDetails)?.grossDeliveryCost}
                     updateDetailsStore={updateDetailsStore}
                     orderType={data?.orders.find((item) => item.orderUuids === openDetails)?.orderType}
                  />
               )}
               {!!printDetails && (
                  <OrderPrint
                     orderUuids={printDetails}
                     open={!!printDetails}
                     cartDescription={
                        data?.orders.find((product) => product.orderUuids === printDetails)?.cartDescription
                     }
                     clientName={data?.orders.find((product) => product.orderUuids === printDetails)?.clientDetails}
                     contactName={
                        data?.orders.find((product) => product.orderUuids === printDetails)?.clientContactDetails
                     }
                     printType={printType}
                     netDeliveryCost={data?.orders.find((item) => item.orderUuids === printDetails)?.netDeliveryCost}
                     grossDeliveryCost={
                        data?.orders.find((item) => item.orderUuids === printDetails)?.grossDeliveryCost
                     }
                     formattedAdditionalServicesValue={
                        data?.orders.find((item) => item.orderUuids === printDetails)?.formattedAdditionalServicesValue
                     }
                     formattedClientValueWithServices={
                        data?.orders.find((item) => item.orderUuids === printDetails)?.formattedClientValueWithServices
                     }
                     formattedClientValue={
                        data?.orders.find((item) => item.orderUuids === printDetails)?.formattedClientValue
                     }
                     formattedValue={data?.orders.find((item) => item.orderUuids === printDetails)?.formattedValue}
                     title={getTitle()}
                     onClose={() => handleOpenPrint(undefined)}
                     details={savedOrderDetails}
                     updateDetailsStore={updateDetailsStore}
                     orderType={data?.orders.find((item) => item.orderUuids === printDetails)?.orderType}
                  />
               )}
            </>
         ) : (
            <Box sx={{ width: '100%', textAlign: 'center' }}>
               <CircularProgress />
            </Box>
         )}
      </Box>
   )
}

export default OrdersHistory
